import { yupResolver } from '@hookform/resolvers/yup'
import React, { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import FieldDropdownV2 from 'src/components/FormComponents/FieldDropdownV2'
import FieldV2 from 'src/components/FormComponents/FieldV2'
import Suffix from 'src/features/receivement/components/Suffix'
import { formatNumber } from 'src/utils/formatNumber'
import { NegationButton } from '../Components/NegationButton'
import Submit from '../Components/Submit'
import { Fieldset } from './Fieldset/Fieldset'
import { Form } from './Form/Form'
import LabelForDueDateField from 'src/features/receivement/components/LabelForDueDateField'
import InformationField from 'src/features/receivement/components/InformationField'
import ModalAlert from 'src/components/ModalAlert'
import { Typography } from '@mui/material'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import * as S from './FormAddNotFoundBatchKg.styles'
import { schema } from './schema'

const FormAddNotFoundBatchKg = ({ onSuccess, onCancel, deliveryDaysMinExpirationDate }) => {
  const { control, handleSubmit, setFocus, formState, reset, setValue, watch, resetField } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      damageType: 'NENHUM',
      damageQty: 0,
    },
  })

  const tipoAvaria = ['NENHUM', 'DANO']
  
  const weight = watch('weight')
  const dueDate = watch('expirationDate')
  const damageType = watch('damageType')
  const damageQty = watch('damageQty')

  const [damageReadOnly, setDamageReadOnly] = useState(true)
  
  useEffect(() => {
    if (damageType === 'NENHUM') {
      setDamageReadOnly(true)
      resetField('damageQty')
    } else if (damageType === 'DANO') {
      setDamageReadOnly(false)
      setValue('damageQty', '')
    } else if (damageType === 'VENCIDO') {
      setDamageReadOnly(true)
    }
  }, [damageType])

  const [dueDateType, setDueDateType] = useState(0)

  const dueDateTypeInfo = useMemo(() => {
    switch (dueDateType) {
      case 1:
        return {
          title: 'Atenção: Validade próxima!',
          alertIconColor: '#FFC400',
          message: 'Atenção: Esse lote está próximo da validade!',
          fieldLabel: <LabelForDueDateField showAlert alertColor='#FFC400' tooltipMessage='Atenção: Validade baixa' />,
          damage: false
        }
      case 2:
        return {
          title: 'Atenção: Produto vencido!',
          alertIconColor: '#FF0000',
          message: 'Atenção: Esse lote está vencido!',
          fieldLabel: <LabelForDueDateField showAlert alertColor='#FF0000' tooltipMessage='Atenção: Produto vencido' />,
          damage: true
        }
      default:
        return {
          title: '',
          alertIconColor: '#000',
          message: '',
          fieldLabel: <LabelForDueDateField showAlert={false} />,
          damage: false
        }
    }
  }, [dueDateType])

  const resetDueDateAndDamageFields = (newDueDateType) => {
    if (dueDateType === 2 && newDueDateType !== 2) {
      setValue('damageType', 'NENHUM')
      setValue('damageQty', 0)
    }
    setDueDateType(newDueDateType)
  }

  const [showModalAlertDueDate, setShowModalAlertDueDate] = useState(false)

  useEffect(() => {
    if (dueDate) {
      const inputDueDate = new Date(dueDate)
      inputDueDate.setHours(inputDueDate.getHours() + (inputDueDate.getTimezoneOffset() / 60))

      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)

      if (inputDueDate < currentDate) {
        setDueDateType(2)
        setValue('damageType', 'VENCIDO')
        setValue('damageQty', weight)
        return
      }

      if (inputDueDate <= currentDate.setDate(currentDate.getDate() + deliveryDaysMinExpirationDate)) {
        resetDueDateAndDamageFields(1)
        return
      }

      resetDueDateAndDamageFields(0)
      return
    }

    resetDueDateAndDamageFields(0)
  }, [dueDate])

  useEffect(() => {
    if (dueDateTypeInfo.damage) {
      setValue('damageQty', weight)
    }
  }, [weight, setValue])

  const onSubmit = (data) => {
    if (dueDateType !== 0 && !showModalAlertDueDate) {
      setShowModalAlertDueDate(true)
      return
    }

    try {
      const total = data.weight
      const batchAdded = {
        ...data,
        isKg: true,
        total
      }

      reset()
      onSuccess(batchAdded)
    } catch (err) {
      console.error(err)
    } finally {
      setShowModalAlertDueDate(false)
    }
  }

  const enterKeyCode = 13
  const tabKeyCode = 9

  const handleOnKeyDown = (event, nextField) => {
    if ([enterKeyCode, tabKeyCode].includes(event.keyCode) && !event.shiftKey) {
      event.preventDefault()
      setFocus(nextField)
    }
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Fieldset>
          <Suffix suffix={'Kg'}>
            <S.Field
              as={FieldV2}
              control={control}
              type="text"
              labelText="Pesagem em quilos"
              name="weight"
              onInput={formatNumber}
              placeholder="0,000"
              onKeyDown={(e) => handleOnKeyDown(e, "expirationDate")}
              autoFocus
              testId='weight-test-id'
            />
          </Suffix>
          <S.Field
            as={FieldV2}
            control={control}
            onKeyDown={(e) => handleOnKeyDown(e, dueDateTypeInfo.damage ? 'damageQty' : 'damageType')}
            type="date"
            labelText={dueDateTypeInfo.fieldLabel}
            name="expirationDate"
            inputTextColor={dueDateTypeInfo.damage ? 'red' : 'black'}
            testId='expiration-date-test-id'
          />
        </Fieldset>
        <Fieldset>
          {dueDateTypeInfo.damage ? (
            <InformationField
              title='Tipo Avaria'
              info='VENCIDO'
              tooltipInfoMessage='Lote definido automaticamente como vencido'
              testId='automatic-damage-type-test-id'
              sxProps={{
                container: {
                  gap: '.47rem',
                  marginLeft: '.1rem'
                },
                title: {
                  fontSize: '14px',
                  fontWeight: 600,
                },
                containerInfo: {
                  backgroundColor: '#F0F0F0',
                },
              }}
            />
          ) : (
            <S.Field
              as={FieldDropdownV2}
              control={control}
              onKeyDown={(e) => handleOnKeyDown(e, "damageQty")}
              labelText="Tipo avaria"
              type="text"
              name="damageType"
              options={tipoAvaria.map((v) => ({ value: v, label: v }))}
              data-testid='damage-type-test-id'
            />
          )}
          <Suffix suffix={'Kg'}>
            <S.Field
              as={FieldV2}
              control={control}
              type="text"
              labelText="Qtd. avarias"
              name="damageQty"
              onInput={formatNumber}
              placeholder="0,000"
              readOnly={damageReadOnly}
              value={damageQty}
              testId='damage-qty-test-id'
            />
          </Suffix>
        </Fieldset>
        <S.ContainerButtons>
          <Submit
            textWhenEnabled="Incluir"
            textWhenDisabled="Salvando..."
            disabled={formState.isSubmitting}
          />
          <NegationButton
            title="Cancelar"
            onClick={onCancel}
            disabled={formState.isSubmitting}
          />
        </S.ContainerButtons>
      </Form>

      {dueDateType !== 0 && (
        <ModalAlert
          show={showModalAlertDueDate}
          title={dueDateTypeInfo.title}
          centered
          action={handleSubmit(onSubmit)}
          okButtonDisabled={formState.isSubmitting}
          closeButtonAction={() => setShowModalAlertDueDate(false)}
          closeButtonDisabled={formState.isSubmitting}
          customizableBody={
            <S.ModalAlertDueDateContainer>
              <ReportProblemIcon
                sx={{
                  fontSize: 80,
                  color: dueDateTypeInfo.alertIconColor
                }}
                data-testid='modal-alert-icon-test-id'
              />
              <S.ModalAlertDueDateInfo>
                <Typography variant='body1' color='textPrimary' sx={{ fontWeight: 600 }}>
                  {dueDateTypeInfo.message}
                </Typography>
                <Typography variant='body1' color='textPrimary' sx={{ fontWeight: 600 }}>
                  Guarde-o separadamente pois ele poderá ser devolvido pelo time de compras!
                </Typography>
              </S.ModalAlertDueDateInfo>
            </S.ModalAlertDueDateContainer>
          }
          testId='modal-due-date-alert-test-id'
        />
      )}
    </>
  )
}

export { FormAddNotFoundBatchKg }
